import { useQuery } from '@apollo/client';
import { EventViewEntityListPageContent } from 'components/eventView/pages/EVEntityListPageContent';
import { EventViewEntityPageContent } from 'components/eventView/pages/EVEntityPageContent';
import { EventData } from 'components/eventView/pages/eventData';
import { EmbedState, ModalWrapperEmbedded } from 'components/site/modals/ModalWrapperEmbedded';
import {
    GuideQueryView,
    GuideQueryViewVariables
} from 'graphql/query/__generated__/GuideQueryView';
import { GUIDE_QUERY_VIEW } from 'graphql/query/guideQuery';
import { useWorldMapLocalStorage } from 'libs/hooks/ownership/useWorldMapLocalStorage';
import { mergeGuideToEvent } from 'libs/utils/guide/mergeGuideToEvent';
import { EmbedListPage } from 'pages/custom/embed/EmbedListPage';
import { EmbedProgramPage } from 'pages/custom/embed/EmbedProgramPage';
import React, { useCallback, useEffect, useState } from 'react';
import { Route, Routes, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { CtxEventViewProvider } from 'session/ctxEventView';
import styled from 'styled-components/macro';
require('./EmbedPage/resizer');

interface Props {
    className?: string;
}

const EMBED_PADDING = 200;

export const EmbedPage = (props: Props): JSX.Element => {
    const { eventId } = useParams() as { eventId: string };
    const { getTokenForGuideId } = useWorldMapLocalStorage();
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const [embedState, setEmbedState] = useState<EmbedState>({
        windowHeight: 0,
        windowWidth: 0,
        iframeOffset: 0
    });

    useEffect(() => {
        window.addEventListener('message', ({ data }) => {
            if (data.type === 'newOffset') {
                setEmbedState({
                    windowHeight: data.screenHeight,
                    windowWidth: data.screenWidth,
                    iframeOffset: data.iframeOffset
                });
            }
        });
    }, []);

    const guideQuery = useQuery<GuideQueryView, GuideQueryViewVariables>(GUIDE_QUERY_VIEW, {
        variables: {
            guideIdOrPath: eventId,
            token: getTokenForGuideId(eventId)
        }
    });

    const event = EventData[eventId];

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const elementResizeRef: React.RefCallback<HTMLDivElement> = useCallback(
        (node: HTMLDivElement) => {
            if (!node) return;
            const resizeObserver = new ResizeObserver((e) => {
                const newHeight = e[0].contentBoxSize[0].blockSize + EMBED_PADDING;
                if ('parentIFrame' in window) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (window.parentIFrame as any).size(newHeight); // Set height to 100px
                }
            });
            resizeObserver.observe(node);
        },
        []
    );

    if (!guideQuery.data) {
        return null;
    }

    function getModalContent() {
        if (searchParams.has('detailId')) {
            const detailId = searchParams.get('detailId');
            return {
                key: detailId,
                title:
                    guideQuery.data.guideView.stories.find((s) => s.storyId === detailId)?.header
                        ?.title || '',
                content: <EventViewEntityPageContent detailId={detailId} />
            };
        }
        if (searchParams.has('detailEntityType') && searchParams.has('detailEntityId')) {
            if (searchParams.get('detailEntityType') === 'program') {
                const programId = searchParams.get('detailEntityId');
                return {
                    key: programId,
                    title:
                        guideQuery.data.guideView.programItems.find(
                            (s) => s.guideProgramItemId === programId
                        )?.title || '',
                    content: (
                        <EventViewEntityListPageContent
                            entity={searchParams.get('detailEntityType')}
                            entityId={programId}
                            entityListAlign="center"
                        />
                    )
                };
            }
        }
    }

    const modalContent = getModalContent();

    const mergedEvent = mergeGuideToEvent(guideQuery?.data?.guideView, event);

    const eventPath =
        guideQuery?.data?.guideView?.customPath ||
        guideQuery?.data?.guideView?.guideId ||
        event?.guideId;

    return (
        <S_EmbedPage className={props.className}>
            <CtxEventViewProvider
                isWide={true}
                isPreview={false}
                isEmbed={true}
                event={mergedEvent}
                eventPath={eventPath}
                forms={guideQuery.data.guideView.forms}
                worldMap={{
                    ...guideQuery.data.guideView.worldMap,
                    guide: {
                        guideId: guideQuery.data.guideView.guideId,
                        title: guideQuery.data.guideView.title,
                        availableLanguages: guideQuery.data.guideView.availableLanguages,
                        language: guideQuery.data.guideView.language,
                        __typename: 'Guide'
                    }
                }}
                stories={guideQuery.data.guideView.stories}
            >
                {modalContent && (
                    <ModalWrapperEmbedded
                        key={modalContent.key}
                        title={modalContent.title}
                        embedState={embedState}
                        onClose={(e) => {
                            searchParams.delete('markerId');
                            if (searchParams.has('detailId')) {
                                searchParams.delete('detailId');
                            } else {
                                searchParams.delete('detailEntityType');
                                searchParams.delete('detailEntityId');
                            }
                            setSearchParams(searchParams);
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                        content={modalContent.content}
                    />
                )}
                <Routes location={location} key={location.pathname}>
                    <Route
                        path="/program"
                        element={<EmbedProgramPage contentRef={elementResizeRef} />}
                    />
                    {mergedEvent.eventInfo?.otherEntities.map((e) => (
                        <Route
                            key={e.id}
                            path={`/${e.id}`}
                            element={
                                <EmbedListPage
                                    contentRef={elementResizeRef}
                                    entityDefinitionId={e.id}
                                />
                            }
                        />
                    ))}
                </Routes>
            </CtxEventViewProvider>
        </S_EmbedPage>
    );
};

const S_EmbedPage = styled.div`
    width: 100%;
`;
